import { baseGatewayApiV1 } from '@verantos-dev/app-state-base';

const ANNOTATIONS_BASE_PATH = 'av-ir/annotations';

interface Annotation {
  annotationId: string;
  targetId: string;
  userId: string;
  userName: string;
  text: string;
  contentHash: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

interface ThreadMetadata {
  targetId: string;
  resolved: boolean;
  resolvedBy: string | null;
  resolvedAt: string | null;
  stalenessReviewedAt: string | null;
  stalenessReviewedBy: string | null;
}

interface ThreadData {
  metadata: ThreadMetadata;
  comments: Annotation[];
}

interface RecentAnnotationsData {
  annotations: Annotation[];
  threadMetadata: ThreadMetadata[];
}

interface FixtureReview {
  fixtureId: string;
  reviewedBy: string;
  reviewedByName: string;
  reviewedAt: string;
}

export const annotationsApiSlice = baseGatewayApiV1
  .enhanceEndpoints({
    addTagTypes: ['annotations', 'annotationThreads', 'fixtureReviews'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getThread: builder.query<ThreadData, string>({
        query: (targetId) => `/${ANNOTATIONS_BASE_PATH}/${encodeURIComponent(targetId)}`,
        providesTags: (_result, _error, targetId) => [{ type: 'annotationThreads', id: targetId }],
      }),

      getRecentAnnotations: builder.query<RecentAnnotationsData, string>({
        query: (since) => `/${ANNOTATIONS_BASE_PATH}/recent?since=${encodeURIComponent(since)}`,
      }),

      createAnnotation: builder.mutation<
        Annotation,
        { targetId: string; text: string; contentHash: string }
      >({
        query: (body) => ({
          url: `/${ANNOTATIONS_BASE_PATH}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { targetId }) => [
          { type: 'annotationThreads', id: targetId },
        ],
      }),

      updateAnnotation: builder.mutation<
        Annotation,
        { targetId: string; annotationId: string; text: string }
      >({
        query: ({ targetId, annotationId, text }) => ({
          url: `/${ANNOTATIONS_BASE_PATH}/${encodeURIComponent(targetId)}/${annotationId}`,
          method: 'PATCH',
          body: { text },
        }),
        invalidatesTags: (_result, _error, { targetId }) => [
          { type: 'annotationThreads', id: targetId },
        ],
      }),

      deleteAnnotation: builder.mutation<Annotation, { targetId: string; annotationId: string }>({
        query: ({ targetId, annotationId }) => ({
          url: `/${ANNOTATIONS_BASE_PATH}/${encodeURIComponent(targetId)}/${annotationId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, { targetId }) => [
          { type: 'annotationThreads', id: targetId },
        ],
      }),

      toggleResolve: builder.mutation<ThreadMetadata, { targetId: string; resolved: boolean }>({
        query: ({ targetId, resolved }) => ({
          url: `/${ANNOTATIONS_BASE_PATH}/${encodeURIComponent(targetId)}/resolve`,
          method: 'PATCH',
          body: { resolved },
        }),
        invalidatesTags: (_result, _error, { targetId }) => [
          { type: 'annotationThreads', id: targetId },
        ],
      }),

      dismissStaleness: builder.mutation<ThreadMetadata, string>({
        query: (targetId) => ({
          url: `/${ANNOTATIONS_BASE_PATH}/${encodeURIComponent(targetId)}/dismiss-staleness`,
          method: 'PATCH',
        }),
        invalidatesTags: (_result, _error, targetId) => [
          { type: 'annotationThreads', id: targetId },
        ],
      }),

      getFixtureReviews: builder.query<FixtureReview[], void>({
        query: () => `/${ANNOTATIONS_BASE_PATH}/fixtures/reviews`,
        providesTags: ['fixtureReviews'],
      }),

      reviewFixture: builder.mutation<FixtureReview, { fixtureId: string; reviewed: boolean }>({
        query: ({ fixtureId, reviewed }) => ({
          url: `/${ANNOTATIONS_BASE_PATH}/fixtures/${encodeURIComponent(fixtureId)}/review`,
          method: 'PATCH',
          body: { reviewed },
        }),
        invalidatesTags: ['fixtureReviews'],
      }),
    }),
  });

export const {
  useGetThreadQuery,
  useGetRecentAnnotationsQuery,
  useCreateAnnotationMutation,
  useUpdateAnnotationMutation,
  useDeleteAnnotationMutation,
  useToggleResolveMutation,
  useDismissStalenessMutation,
  useGetFixtureReviewsQuery,
  useReviewFixtureMutation,
} = annotationsApiSlice;
